// src/Dashboard/pages/user/Reglements/utils/reglementsUtils.js

import { toAbsoluteArticlePhotoUrl } from "../../../../../services/articlesPhotoServices";

export const PAGE_SIZE = 12;

export const DEFAULT_REGLEMENTS_UI = {
  search: "",
  status: "all",
  season: "all",
  focus: "all", // all | toPay | paid | installments
  sort: "dateDesc", // dateDesc | dateAsc | amountDesc | amountAsc | alpha
  perPage: PAGE_SIZE,
};

export const REGLEMENTS_UI_STORAGE_KEY = "reglements_dashboard_ui_state";
export const NOTIFIED_STORAGE_KEY = "notifiedReglementIds";

export const FALLBACK_IMAGE = "/img/no-image.jpg";

export const PAID_STATUSES = ["Réglé", "Partiellement Réglé"];
export const PAYABLE_STATUSES = ["Non Réglé", "En Attente"];

export const collatorFR = new Intl.Collator("fr", { sensitivity: "base" });

export const strip = (value = "") =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

export const readStorageJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const writeStorageJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

export const toNumberSafe = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const normalizePositiveInteger = (value, fallback = 1) => {
  const number = Number(value);

  if (!Number.isFinite(number)) return fallback;

  const integer = Math.floor(number);

  return integer > 0 ? integer : fallback;
};

export const canUseInstallmentPayments = (maxPayments) => {
  return normalizePositiveInteger(maxPayments, 1) > 1;
};

export const formatEUR = (value) => {
  return toNumberSafe(value).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
  });
};

export const formatDisplayDate = (dateString) => {
  if (!dateString) return "Date inconnue";

  try {
    return new Intl.DateTimeFormat("fr-FR", {
      dateStyle: "medium",
    }).format(new Date(dateString));
  } catch {
    return "Date inconnue";
  }
};

export const determineSeason = (dateString) => {
  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) return "Saison inconnue";

  const year = date.getFullYear();
  const seasonStart = new Date(`${year}-08-01`);
  const seasonEnd = new Date(`${year + 1}-07-31`);

  if (date >= seasonStart && date <= seasonEnd) {
    return `Saison ${year}-${year + 1}`;
  }

  return `Saison ${year - 1}-${year}`;
};

export const isRecent = (dateString, minutes = 15) => {
  if (!dateString) return false;

  const time = new Date(dateString).getTime();

  if (Number.isNaN(time)) return false;

  return Date.now() - time <= minutes * 60 * 1000;
};

export const hasEcheanceTag = (message = "") => {
  return strip(message).includes("echeance");
};

export const getArticleName = (reglement) => {
  if (reglement?.article_id === null) {
    return "Règlement Licence-Cotisation";
  }

  return reglement?.Article?.nom || reglement?.nomArticle || "Règlement";
};

export const getArticleMessage = (reglement) => {
  return String(reglement?.message_achat || "").trim();
};

export const getArticlePhoto = (reglement, defaultPhotoUrl = "") => {
  const raw =
    reglement?.Article?.photo_url ||
    reglement?.photoUrl ||
    defaultPhotoUrl ||
    "";

  return raw ? toAbsoluteArticlePhotoUrl(raw) : FALLBACK_IMAGE;
};

export const computeUnitPrice = (reglement) => {
  const quantity = Math.max(1, toNumberSafe(reglement?.quantite));
  const total = toNumberSafe(reglement?.prix_total);
  const unitFromReglement = toNumberSafe(reglement?.prix_unitaire);
  const unitFromArticle = toNumberSafe(reglement?.Article?.prix);

  const unit =
    unitFromReglement || unitFromArticle || (quantity ? total / quantity : total);

  return Number(unit.toFixed(2));
};

export const canPayReglement = (reglement) => {
  return (
    PAYABLE_STATUSES.includes(reglement?.statut_reglement) &&
    reglement?.autorisation_achat !== false
  );
};

export const canShowPaymentButtons = (reglement) => {
  return PAYABLE_STATUSES.includes(reglement?.statut_reglement);
};

export const canPayInInstallments = (reglement, maxPayments = 1) => {
  if (!reglement) return false;

  const installmentsEnabled = canUseInstallmentPayments(maxPayments);

  if (!installmentsEnabled) return false;

  const isLicenceCotisation = reglement?.article_id === null;
  const isAlreadyInstallment = hasEcheanceTag(reglement?.message_achat);

  return isLicenceCotisation && !isAlreadyInstallment;
};

export const getStatusMeta = (status = "") => {
  switch (status) {
    case "Non Réglé":
      return {
        label: "Non réglé",
        dot: "bg-red-500",
        badge:
          "bg-red-50 text-red-700 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20",
        card:
          "border-red-200 bg-red-50 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300",
      };

    case "Réglé":
      return {
        label: "Réglé",
        dot: "bg-emerald-500",
        badge:
          "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
        card:
          "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
      };

    case "En Attente":
      return {
        label: "En attente",
        dot: "bg-amber-500",
        badge:
          "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
        card:
          "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
      };

    case "Annulé":
      return {
        label: "Annulé",
        dot: "bg-slate-900",
        badge:
          "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700",
        card:
          "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200",
      };

    case "Remboursé":
      return {
        label: "Remboursé",
        dot: "bg-purple-500",
        badge:
          "bg-purple-50 text-purple-700 ring-purple-200 dark:bg-purple-500/10 dark:text-purple-300 dark:ring-purple-500/20",
        card:
          "border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-500/20 dark:bg-purple-500/10 dark:text-purple-300",
      };

    case "Partiellement Réglé":
      return {
        label: "Partiellement réglé",
        dot: "bg-lime-500",
        badge:
          "bg-lime-50 text-lime-700 ring-lime-200 dark:bg-lime-500/10 dark:text-lime-300 dark:ring-lime-500/20",
        card:
          "border-lime-200 bg-lime-50 text-lime-700 dark:border-lime-500/20 dark:bg-lime-500/10 dark:text-lime-300",
      };

    default:
      return {
        label: status || "Inconnu",
        dot: "bg-slate-400",
        badge:
          "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700",
        card:
          "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200",
      };
  }
};

export const buildReglementSearchText = (reglement) => {
  return [
    getArticleName(reglement),
    getArticleMessage(reglement),
    reglement?.saison,
    reglement?.statut_reglement,
    reglement?.type_reglement,
    reglement?.type_achat,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

export const prepareReglement = (reglement, defaultPhotoUrl = "") => {
  return {
    ...reglement,
    saison: determineSeason(reglement?.date_achat),
    nomArticle: getArticleName(reglement),
    photoUrl: getArticlePhoto(reglement, defaultPhotoUrl),
  };
};

export const canGenerateInvoice = (reglement) => {
  const isBoutiqueArticle =
    reglement?.article_id !== null && reglement?.article_id !== undefined;

  return isBoutiqueArticle && reglement?.statut_reglement === "Réglé";
};